// We probably should go ahead and import those types to make sure they remain the same in the future
import React, { useCallback, useRef, useState } from 'react';

import { transformCurrency } from '@givebacks/utils/currency';
import { DateTime } from 'luxon';

import { phoneNumberDisplayFormat } from '../format';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RowData = unknown | object | any[];

export type UseCreateCSVProps<TData extends RowData> = {
  fileName?: string;
  data?: TData[];
  columns: Column<TData>[];
};

type Column<TData> = {
  // accessor?: DeepKeys<TData>;
  // headerTranslations?: Map<string, string>; // Upcoming Feature
  columnFormat?:
    | 'dateTime'
    | 'phoneNumber'
    | 'currency'
    | 'currency_formatted'
    | 'additionalFields';
  header: string;
  cell: (row: TData) => React.ReactNode; // A cell is used so that you can extract any combination of values from Row  that you want. You can also use this to format the data in the cell.
  visible?: boolean;
};

type CreateColumnCreator<TData> = {
  create: (column: Column<TData>) => Column<TData>;
};

/**
 * Converts a cell value to a string.
 * @param value - The cell value to convert to a string.
 * @param delimiter - The delimiter to use when converting an array to a string.
 * @returns The cell value as a string.
 */
const cellToString = (value: string | number | string[], delimiter: string = ', '): string => {
  if (Array.isArray(value)) {
    return value.join(delimiter);
  }
  if (typeof value == 'string' || typeof value == 'number') {
    return value.toString();
  }

  return '';
};

/**
 * We'll use this to create an easily accessible way to format simple cell data to popular format.
 * This is optional.
 * I could see this being useful for phone numbers, dates, casing, currency, etc.
 */
const commonColumnCellTransformers = new Map<
  string,
  (data: string | number | string[]) => string | number
>([
  ['additionalFields', cellToString],
  ['phoneNumber', (data) => phoneNumberDisplayFormat(cellToString(data, ''))],
  ['dateTime', (data) => DateTime.fromISO(cellToString(data, '')).toLocaleString()],
  ['currency', (data) => transformCurrency(cellToString(data, ''), 'dollars')],
  ['currency_formatted', (data) => `$ ${transformCurrency(cellToString(data, ''), 'dollars')}`],
]);

export const escapedString = (string: string | React.ReactNode) => {
  if (string === undefined || string === null) return '';
  if (typeof string === 'object' || typeof string === 'function') return '';
  let escapedString = '';
  if (typeof string !== 'number' || typeof string !== 'boolean') {
    escapedString = String(string);
  }

  return `"${escapedString}"`;
};

function createColumnCreator<data>(): CreateColumnCreator<data> {
  const createCell = (column: Column<data>, row: data) => {
    const cellNode = column.cell(row);
    const transformedCell = column.columnFormat
      ? commonColumnCellTransformers.get(column.columnFormat)?.(String(cellNode))
      : cellNode;

    return escapedString(transformedCell);
  };

  return {
    create: (column) => ({
      header: escapedString(column.header),
      cell: (row) => createCell(column, row),
      visible: column.visible ?? true,
    }),
  };
}
const columnsCreator = createColumnCreator;

const downloadFile = (fileName: string, encodedData: string) => {
  const link = document.createElement('a');
  link.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodedData);
  link.setAttribute('download', `${fileName}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Non state managed version for use in non react components
const createCsv = <T extends RowData>({
  fileName = 'data',
  columns,
  data,
}: UseCreateCSVProps<T>) => {
  let headers: unknown[] = [];
  let rows;
  let csvContent = '';
  const generate = (callback?: (fileName: string, csv: string) => void) => {
    const visibleColumns = columns.filter((column) => column.visible !== false);
    headers = visibleColumns.map((column) => column.header);
    rows = data?.map((row) => visibleColumns.map((column) => column.cell(row)));
    // rows = data?.map((row) => columns.map((column) => column.cell(row, column.transformCell(row))));
    csvContent = [headers, ...rows].join('\r\n');
    const encodedUri = encodeURIComponent(csvContent);
    callback?.(fileName, encodedUri);
  };
  const download = (callback?: (fileName: string, csv: string) => void) => {
    generate((fileName, csvData) => {
      downloadFile(fileName, csvData);
      callback?.(fileName, csvData);
    });
  };

  return { csvContent, headers, rows, generate, download };
};

// A hooked version for use in react components
const useCreateCsv = <T extends RowData>({ fileName, columns, data }: UseCreateCSVProps<T>) => {
  const columnsRef = useRef(columns ?? []);
  const _dataRef = useRef<T[]>(data ?? []);
  const [csv, setCsv] = useState<string>('');

  const generate = useCallback(() => {
    const { generate: generateCsv } = createCsv({
      fileName,
      columns: columnsRef.current,
      data: _dataRef.current,
    });

    generateCsv((_, csvData) => {
      setCsv(csvData);
    });
  }, [fileName]);

  const download = useCallback(() => {
    const { download: downloadCsv } = createCsv({
      fileName,
      columns: columnsRef.current,
      data: _dataRef.current,
    });
    downloadCsv();
  }, [fileName]);

  return {
    generate,
    download,
    csv,
    fileName,
    setData: useCallback((data: T[]) => (_dataRef.current = data), []),
    setColumns: useCallback((columns: Column<T>[]) => (columnsRef.current = columns), []),
  };
};

const splitRowData = (row: string): string[] => {
  /* This regex matches fields in CSV data:
   * (?<=^|,)      - Positive lookbehind: match must be preceded by start of line or comma
   * (?:           - Non-capturing group for the entire field
   *   "([^"]*)"   - Quoted field: captures content between quotes in group 1
   *   |           - OR
   *   [^,]*       - Unquoted field: any characters except comma
   * )
   */
  const regex = /(?<=^|,)(?:"([^"]*)"|[^,]*)/g;

  const matches = row.matchAll(regex);
  return Array.from(matches).map((match) => {
    // If we captured a quoted value (match[1]), use that, otherwise use the full match (match[0])
    const value = match[1] !== undefined ? match[1] : match[0];
    return value.trim();
  });
};

const processCSV = (dataString: string, delim = ',', skipEmptyRows = true) => {
  const headers = dataString.slice(0, dataString.indexOf('\n')).split(delim);
  const rows = dataString.slice(dataString.indexOf('\n') + 1).split('\n');

  const newArray: Record<string, string | undefined>[] = rows.reduce(
    (accumulator: Record<string, string | undefined>[], row) => {
      const values = splitRowData(row);
      // a row is empty if after it's split at `delim` it is an empty array. An array with only a carriage return will return an empty string when trimmed.
      const isEmptyRow = values.filter((value) => value.trim() !== '').length === 0;
      // skip empty rows
      if (skipEmptyRows && isEmptyRow) {
        return accumulator;
      }

      const eachObject = headers.reduce(
        (object: Record<string, string | undefined>, header, index) => {
          const objectHeader = header.trim() as keyof object;
          object[objectHeader] = values[index]
            ? decodeURI(values[index]?.replace(/\r\n/g, '\n').trim())
            : undefined;
          return object;
        },
        {}
      );

      accumulator.push(eachObject);

      return accumulator;
    },
    []
  );

  return newArray;
};

const importCsv = (
  file: File,
  callback: (data: Record<string, string | undefined>[], headers: string[]) => void
) => {
  const reader = new FileReader();
  reader.onload = () => {
    const text = reader.result;
    // Force the text to be a string and trim it to prevent rogue spaces from causing new entries
    const trimmedText = text?.toString().trim() ?? '';
    const data = processCSV(trimmedText);
    const headers = trimmedText.slice(0, trimmedText.indexOf('\n')).split(',');

    callback(data, headers);
  };
  reader.readAsText(file);
};

export { columnsCreator, createCsv, importCsv, processCSV, splitRowData, useCreateCsv };
export type { Column, CreateColumnCreator, RowData };

// Test type to test out deep keys
// export type Person = {
//   id: number;
//   firstName: string;
//   lastName: string;
//   age: number;
//   visits: number;
//   status: string;
//   progress: number;
//   createdAt: Date;
//   donation: number;
//   nested: {
//     foo: [
//       {
//         bar: 'bar';
//       }
//     ];
//     bar: { subBar: boolean }[];
//     baz: {
//       foo: 'foo';
//       bar: {
//         baz: 'baz';
//       };
//     };
//   };
// };
